import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import * as signalR from '@microsoft/signalr';
import { ContentScoringService } from 'listings/services/content-scoring.service';
import { ListingValidationService } from 'listings/services/listing-validation.service';
import { ListingsScrollUtilsService } from 'listings/services/listingsScrollUtils.service';
import { WebsocketConnectionKeys } from 'shared/enums/websocket-connection-keys.enum';
import { TokenService } from '../../authentication/services/token.service';
import { EventBusService } from './event-bus.service';

const HUB_URLS = {
    USERS: environment.BASE_URL + 'userApi/userNotificationsHub',
    LISTINGS: environment.BASE_URL + 'listingApi/listingNotificationsHub',
    DISTRIBUTION_TRACKING: environment.BASE_URL + 'listingApi/listingNotificationsHub',
    CONTENT_SCORE: environment.BASE_URL + 'listingApi/listingNotificationsHub',
    LISTING_SNAPSHOTS: environment.BASE_URL + 'listingApi/listingNotificationsHub',
    LISTING_VALIDATION: environment.BASE_URL + 'listingApi/listingNotificationsHub',
};

const methodNames = {
    receiveNotification: 'ReceiveNotification',
    updateListingStatus: 'UpdateListingStatus',
    updateListingVisualsStatus: 'UpdateListingVisualsStatus',
    updateChannelSnapshotStatus: 'UpdateListingSnapshotStatus',
    updateListingDistributionTracking: 'UpdateListingDistributionTracking',
    updateListingContentScore: 'UpdateListingContentScore',
    updateListingValidationResult: 'UpdateListingValidationResult',
    authorizeClientAsync: 'AuthorizeClientAsync',
    getListingStatusAsync: 'GetListingStatusAsync',
};

@Injectable({
    providedIn: 'root',
})
export class SignalRService {
    private hubConnections: Partial<Record<WebsocketConnectionKeys, signalR.HubConnection | undefined>> = {};
    private readonly keepAliveInterval = 15000;
    private handlerRegistry: { [key: string]: boolean } = {};
    private intentionalDisconnections: { [key: string]: boolean } = {};
    private messageQueue: Partial<Record<WebsocketConnectionKeys, { method: string; message: string | string[] }[]>> = {};

    constructor(
        private eventBusService: EventBusService,
        private tokenService: TokenService,
        private contentScoringService: ContentScoringService,
        private listingsScrollUtilsService: ListingsScrollUtilsService,
        private listingValidationService: ListingValidationService,
    ) {}

    public startConnectionByKey(key: WebsocketConnectionKeys) {
        switch (key) {
            case WebsocketConnectionKeys.USERS:
                this.startConnection(key, HUB_URLS.USERS, this.addUserNotificationHandlers.bind(this));
                break;
            case WebsocketConnectionKeys.LISTINGS:
                this.startConnection(key, HUB_URLS.LISTINGS, this.addListingNotificationHandlers.bind(this));
                break;
            case WebsocketConnectionKeys.DISTRIBUTION_TRACKING:
                this.startConnection(key, HUB_URLS.DISTRIBUTION_TRACKING, this.addDistributionTrackingNotificationHandlers.bind(this));
                break;
            case WebsocketConnectionKeys.CONTENT_SCORE:
                this.startConnection(key, HUB_URLS.CONTENT_SCORE, this.addContentScoring.bind(this));
                break;
            case WebsocketConnectionKeys.LISTING_SNAPSHOTS:
                this.startConnection(key, HUB_URLS.LISTING_SNAPSHOTS, this.addListingSnapshotNotificationHandlers.bind(this));
                break;
            case WebsocketConnectionKeys.LISTING_VALIDATION:
                this.startConnection(key, HUB_URLS.LISTING_VALIDATION, this.addListingValidationHandlers.bind(this));
                break;
        }
    }

    private startConnection(key: WebsocketConnectionKeys, url: string, handlerFn: () => void) {
        const connection = this.hubConnections[key];

        if (connection && this.isConnectingOrConnected(connection)) {
            return;
        }

        if (!this.tokenService.token()) {
            return;
        }

        this.hubConnections[key] = new signalR.HubConnectionBuilder()
            .withUrl(url, {
                transport: signalR.HttpTransportType.WebSockets,
                skipNegotiation: true,
            })
            .withAutomaticReconnect([0, 2000, 10000, 30000])
            .build();

        this.hubConnections[key]
            ?.start()
            .then(() => {
                this.sendMessageToServer(this.hubConnections[key]!, methodNames.authorizeClientAsync, this.tokenService.token()!, key);
                this.startKeepAlive(this.hubConnections[key]!);
                handlerFn();
                this.retryMessageQueue(key);
            })
            .catch((err) => console.error(`Error while starting '${key.toLowerCase()}' connection: `, err));

        this.hubConnections[key]?.onreconnected(() => {
            this.sendMessageToServer(this.hubConnections[key]!, methodNames.authorizeClientAsync, this.tokenService.token()!, key);
        });

        this.hubConnections[key]?.onclose(() => {
            if (this.intentionalDisconnections[key]) {
                console.info(`'${key}' connection closed intentionally.`);
                return; // Skip reconnection logic
            }

            console.info(`'${key}' connection closed, attempting to reconnect...`);
            // Timeout OK
            setTimeout(() => this.startConnection(key, url, handlerFn), 5000);
        });
    }

    private retryMessageQueue(key: WebsocketConnectionKeys) {
        const queuedMessageByKey = this.messageQueue[key];
        const connection = this.hubConnections[key];
        if (queuedMessageByKey?.length && connection && connection.state === signalR.HubConnectionState.Connected) {
            queuedMessageByKey.forEach(({ method, message }) => {
                this.sendMessageToServer(connection, method, message, key);
            });
            this.messageQueue[key] = [];
        }
    }

    public sendMessageToServer(
        connection: signalR.HubConnection,
        methodName: string,
        message: string | string[],
        key: WebsocketConnectionKeys,
    ) {
        if (connection.state === signalR.HubConnectionState.Disconnected) {
            this.startConnectionByKey(key);
            return;
        }

        if (connection.state === signalR.HubConnectionState.Connected) {
            connection
                .invoke(methodName, ...(Array.isArray(message) ? message : [message]))
                .catch((err) => console.error(`Error while sending message to '${methodName}' method:`, err));
        }
    }

    public sendMessageToServerByKey(key: WebsocketConnectionKeys, methodName: string, message: string | string[]) {
        const connection = this.hubConnections[key];
        if (connection && connection.state === signalR.HubConnectionState.Connected) {
            this.sendMessageToServer(connection, methodName, message, key);
        } else {
            const messagePayload = { method: methodName, message: message };
            const queue = this.messageQueue[key];
            if (queue) {
                queue.push(messagePayload);
            } else {
                this.messageQueue[key] = [messagePayload];
            }
        }
    }

    public stopConnections() {
        Object.keys(this.hubConnections).forEach((key) => {
            this.stopConnectionByKey(key as WebsocketConnectionKeys);
        });
    }

    public stopConnectionByKey(key: WebsocketConnectionKeys) {
        const connection = this.hubConnections[key];
        if (connection && connection.state === signalR.HubConnectionState.Connected) {
            // Mark the disconnection as intentional
            this.intentionalDisconnections[key] = true;

            connection
                .stop()
                .then(() => {
                    this.handlerRegistry[key] = false;
                    this.intentionalDisconnections[key] = false; // Reset the flag after stopping
                })
                .catch((err) => console.error(`Error stopping '${key.toLowerCase()}' connection:`, err));
        }
    }

    private startKeepAlive(connection: signalR.HubConnection) {
        setInterval(() => {
            if (connection.state === signalR.HubConnectionState.Connected) {
                connection.invoke('KeepAlive').catch((err) => console.error('KeepAlive failed:', err));
            }
        }, this.keepAliveInterval);
    }

    private isConnectingOrConnected(connection: signalR.HubConnection): boolean {
        return [
            signalR.HubConnectionState.Connecting,
            signalR.HubConnectionState.Connected,
            signalR.HubConnectionState.Reconnecting,
        ].includes(connection.state);
    }

    private addUserNotificationHandlers() {
        const connection = this.hubConnections[WebsocketConnectionKeys.USERS];
        if (!this.handlerRegistry[WebsocketConnectionKeys.USERS] && connection) {
            connection.on(methodNames.receiveNotification, (notificationData) => {
                if (notificationData) {
                    const notification = JSON.parse(notificationData);
                    this.eventBusService.dispatch('notification', notification);
                }
            });
            this.handlerRegistry[WebsocketConnectionKeys.USERS] = true;
        }
    }

    private addListingNotificationHandlers() {
        const connection = this.hubConnections[WebsocketConnectionKeys.LISTINGS];
        if (!this.handlerRegistry[WebsocketConnectionKeys.LISTINGS] && connection) {
            connection.on(methodNames.updateListingStatus, (data) => {
                this.eventBusService.dispatch('listing:statusUpdate', data);
            });
            connection.on(methodNames.updateListingVisualsStatus, (data) => {
                this.eventBusService.dispatch('listing:visualsStatusUpdate', data);
            });
            this.handlerRegistry[WebsocketConnectionKeys.LISTINGS] = true;
        }
    }

    private addDistributionTrackingNotificationHandlers() {
        const connection = this.hubConnections[WebsocketConnectionKeys.DISTRIBUTION_TRACKING];
        if (!this.handlerRegistry[WebsocketConnectionKeys.DISTRIBUTION_TRACKING] && connection) {
            connection.on(methodNames.updateListingDistributionTracking, (data) => {
                this.eventBusService.dispatch('listing:distributionTrackingStatusUpdate', data);
            });
            this.handlerRegistry[WebsocketConnectionKeys.DISTRIBUTION_TRACKING] = true;
        }
    }

    private addContentScoring() {
        const connection = this.hubConnections[WebsocketConnectionKeys.CONTENT_SCORE];
        if (!this.handlerRegistry[WebsocketConnectionKeys.CONTENT_SCORE] && connection) {
            connection.on(methodNames.updateListingContentScore, (data: string) => {
                const unescapedObject: string = JSON.parse(data);
                const parsedObject: any = JSON.parse(unescapedObject);
                // The same object received on the REST call on init has the object keys as camelCase, but here it's received as PascalCase
                // Because of this, we need to map the object keys to camelCase
                const mappedData = this.contentScoringService.mapContentScoringPascalCaseObjectToCamelCase(parsedObject);
                const listingId = parsedObject.ListingId;
                if (listingId === this.contentScoringService.currentlyLoadedListingId()) {
                    this.contentScoringService.setScoreResult(mappedData);
                    this.listingsScrollUtilsService.removeScrollToClass(mappedData);
                }
            });
            this.handlerRegistry[WebsocketConnectionKeys.CONTENT_SCORE] = true;
        }
    }

    private addListingValidationHandlers() {
        const connection = this.hubConnections[WebsocketConnectionKeys.LISTING_VALIDATION];
        if (!this.handlerRegistry[WebsocketConnectionKeys.LISTING_VALIDATION] && connection) {
            connection.on(methodNames.updateListingValidationResult, (data: string | object) => {
                const parsedObject: any = typeof data === 'string' ? JSON.parse(data) : data;
                const mapped = this.listingValidationService.mapPascalCaseToCamelCase(parsedObject);

                if (mapped.listingId === this.listingValidationService.currentlyLoadedListingId()) {
                    this.listingValidationService.setValidationResult(mapped);
                }
            });
            this.handlerRegistry[WebsocketConnectionKeys.LISTING_VALIDATION] = true;
        }
    }

    private addListingSnapshotNotificationHandlers() {
        const connection = this.hubConnections[WebsocketConnectionKeys.LISTING_SNAPSHOTS];
        if (!this.handlerRegistry[WebsocketConnectionKeys.LISTING_SNAPSHOTS] && connection) {
            connection.on(methodNames.updateChannelSnapshotStatus, (data) => {
                this.eventBusService.dispatch('listing:snapshotStatusUpdate', data);
            });
            this.handlerRegistry[WebsocketConnectionKeys.LISTING_SNAPSHOTS] = true;
        }
    }
}
